import React from 'react';
import { cn } from '../../../lib/utils';

export interface TaskySegmentOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface TaskySegmentedControlProps<T extends string = string> {
  options: TaskySegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** 既定 'md' */
  size?: 'md' | 'sm';
  className?: string;
  'aria-label'?: string;
}

/** DS 正本 `#tabs` / `#segmented` 共通のカウントバッジ。 */
const countBadgeClass =
  'ml-1.5 inline-flex min-w-[20px] justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold leading-5 text-white';

const buttonSizeClass = {
  md: 'px-4 py-2 text-sm',
  sm: 'px-3 py-1 text-xs',
} as const;

export function TaskySegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: TaskySegmentedControlProps<T>): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex bg-gray-100 border border-warm-border rounded-tasky-sm p-1 gap-1',
        className
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            onClick={() => {
              if (option.disabled || selected) return;
              onChange(option.value);
            }}
            className={cn(
              'inline-flex items-center justify-center bg-white rounded-tasky-xs font-medium transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              buttonSizeClass[size],
              selected && 'bg-primary text-white',
              option.disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {option.label}
            {option.count !== undefined && (
              <span
                data-testid={`tasky-segment-count-${option.value}`}
                className={cn(countBadgeClass, option.count === 0 && 'invisible')}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
